import React from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Stack, Checkbox, List, ListItem, ListItemText, Table, TableHead, TableRow, TableCell, TableBody, TextField, Select, MenuItem, IconButton, Chip, Paper, Tooltip, Card, CardContent, Menu, ListItemIcon, InputAdornment, Collapse } from '@mui/material';
import { AppDialog } from '../components/dialog';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import WarningRounded from '@mui/icons-material/WarningRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import SortRounded from '@mui/icons-material/SortRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import LabelImportantRounded from '@mui/icons-material/LabelImportantRounded';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildApiUrl } from '../config';
import { ModeContext } from '../theme/ThemeProvider';
import { formatAbsolute, formatRelativeTime } from '../utils/date';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { ConnectorIcon, prettyConnectorType } from '../utils/connectorMeta';

// ── Tag semantic mapping ──────────────────────────────────────────────────
// Tags carry meaning. We translate a small set of well-known tags to a
// coloured chip so users can spot risk/role at a glance without reading text.
type TagTone = 'pii' | 'core' | 'transactional' | 'reference' | 'unassigned' | 'neutral';
function classifyTag(raw: string): TagTone {
  const t = String(raw || '').toLowerCase().trim();
  if (!t) return 'neutral';
  if (t === 'pii' || t === 'sensitive' || t === 'phi') return 'pii';
  if (t === 'core' || t === 'primary') return 'core';
  if (t === 'transactional' || t === 'event' || t === 'fact') return 'transactional';
  if (t === 'reference' || t === 'lookup' || t === 'dimension') return 'reference';
  if (t === 'unassigned') return 'unassigned';
  return 'neutral';
}
function tagChipProps(tone: TagTone): { color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'; icon?: React.ReactElement } {
  switch (tone) {
    case 'pii': return { color: 'warning', icon: <ShieldRounded sx={{ fontSize: 12 }} /> };
    case 'core': return { color: 'primary' };
    case 'transactional': return { color: 'info' };
    case 'reference': return { color: 'default' };
    case 'unassigned': return { color: 'warning' };
    default: return { color: 'default' };
  }
}

export default function DataRelationshipsPage() {
  // Pilot-only: saved canonical is expected to have entities/relationships
  const navigate = useNavigate();
  const location = useLocation();
  const { branding } = React.useContext(ModeContext);
  const { notify } = React.useContext(SnackbarContext);
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [tenantName, setTenantName] = React.useState<string | null>(null);
  const [connectors, setConnectors] = React.useState<any[]>([]);
  const [selectedConnectorIds, setSelectedConnectorIds] = React.useState<string[]>([]);
  const [domainContext, setDomainContext] = React.useState<string>('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);
  const [reviewData, setReviewData] = React.useState<any | null>(null);
  const [canonicalSaving, setCanonicalSaving] = React.useState(false);
  const [canonicalMessage, setCanonicalMessage] = React.useState<string | null>(null);
  const [savedCanonical, setSavedCanonical] = React.useState<any | null>(null);
  const [savedCanonicalVersion, setSavedCanonicalVersion] = React.useState<number | null>(null);
  // Captured separately so the freshness chip stays accurate across refreshes;
  // backend may return any of created_at/updated_at/saved_at — we accept all.
  const [savedCanonicalUpdatedAt, setSavedCanonicalUpdatedAt] = React.useState<string | null>(null);
  const [savedCanonicalLoading, setSavedCanonicalLoading] = React.useState(false);
  const [draftCanonical, setDraftCanonical] = React.useState<any | null>(null);
  const [savedBaseSchemaIds, setSavedBaseSchemaIds] = React.useState<string[] | null>(null);

  // Workbench controls: search/sort/source-filter + overflow menu.
  // The "tag filter" pill row was removed — it was rarely used and added
  // visual noise above an already busy toolbar; tags are still shown on the
  // cards themselves for quick scanning.
  const [moreMenuAnchor, setMoreMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [sortMenuAnchor, setSortMenuAnchor] = React.useState<null | HTMLElement>(null);
  type EntitySortKey = 'name' | 'fields_desc' | 'fields_asc' | 'updated';
  const [sortKey, setSortKey] = React.useState<EntitySortKey>('fields_desc');
  const [sourceFilter, setSourceFilter] = React.useState<string>('all');
  // Collapsed groups in the relationship panel (key = source-entity name)
  const [collapsedRelGroups, setCollapsedRelGroups] = React.useState<Record<string, boolean>>({});

  // Entity edit modal state (minimal - saving/messages are now in modal)
  const [entityModalOpen, setEntityModalOpen] = React.useState(false);
  const [selectedEntityIndex, setSelectedEntityIndex] = React.useState<number | null>(null);
  const [modalEntityData, setModalEntityData] = React.useState<any | null>(null);

  // Relationship edit modal state (minimal - saving/messages are now in modal)
  const [relationshipModalOpen, setRelationshipModalOpen] = React.useState(false);
  const [selectedRelationshipIndex, setSelectedRelationshipIndex] = React.useState<number | null>(null);
  const [modalRelationshipData, setModalRelationshipData] = React.useState<any | null>(null);
  const [isAddingRelationship, setIsAddingRelationship] = React.useState(false);

  // Indexing state
  const [indexBuildLoading, setIndexBuildLoading] = React.useState(false);
  const [indexBuildError, setIndexBuildError] = React.useState<string | null>(null);
  const [indexBuildCounts, setIndexBuildCounts] = React.useState<any | null>(null);
  const [exportLoading, setExportLoading] = React.useState(false);
  
  // Background enrichment tracking
  const [enrichmentStatus, setEnrichmentStatus] = React.useState<any>(null);
  const [pollingInterval, setPollingInterval] = React.useState<NodeJS.Timeout | null>(null);
  
  // Entity search
  const [entitySearch, setEntitySearch] = React.useState<string>('');
  
  // Timer for enrichment elapsed time
  const [enrichmentElapsed, setEnrichmentElapsed] = React.useState<number>(0);
  
  // Update elapsed time every second when enriching
  React.useEffect(() => {
    if (!enrichmentStatus) return;
    
    const updateElapsed = () => {
      const elapsed = Date.now() - (enrichmentStatus.startedAt || Date.now());
      setEnrichmentElapsed(elapsed);
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    
    return () => clearInterval(interval);
  }, [enrichmentStatus]);

  // --------- Simple interactive relationship graph (SVG) ---------
  function RelationshipGraph({ entities, relationships }: { entities: any[]; relationships: any[] }) {
    const theme = useTheme();
    const names: string[] = (entities || []).map((e: any) => String(e.name || ''));
    const n = Math.max(1, names.length);
    const width = 920;
    const height = 380;
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) * 0.38;
    const nodeR = 16;
    const [hovered, setHovered] = React.useState<string | null>(null);

    const pos: Record<string, { x: number; y: number }> = {};
    names.forEach((name, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      pos[name] = { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
    });

    const primary = theme.palette.primary.main;
    const edgeColor = theme.palette.mode === 'light' ? 'rgba(2, 117, 216, 0.55)' : 'rgba(144, 202, 249, 0.55)';
    const mutedEdge = theme.palette.mode === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.18)';
    const nodeFill = theme.palette.mode === 'light' ? theme.palette.background.paper : 'rgba(255,255,255,0.06)';
    const nodeStroke = theme.palette.divider;

    const rels = Array.isArray(relationships) ? relationships : [];

    return (
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} role="img">
          {/* Edges */}
          {rels.map((r: any, idx: number) => {
            const a = String(r.from_entity || '');
            const b = String(r.to_entity || '');
            if (!(a in pos) || !(b in pos)) return null;
            const pa = pos[a];
            const pb = pos[b];
            const active = !hovered || hovered === a || hovered === b;
            const conf = typeof r.confidence === 'number' ? Math.max(0, Math.min(1, r.confidence)) : 0.4;
            const sw = 1 + 2 * conf;
            return (
              <g key={idx}>
                <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={active ? edgeColor : mutedEdge} strokeWidth={sw} />
              </g>
            );
          })}

          {/* Nodes */}
          {names.map((name, i) => {
            const p = pos[name];
            return (
              <g key={name} onMouseEnter={() => setHovered(name)} onMouseLeave={() => setHovered(null)}>
                <Tooltip title={name}>
                  <circle cx={p.x} cy={p.y} r={nodeR} fill={nodeFill} stroke={hovered === name ? primary : nodeStroke} strokeWidth={hovered === name ? 3 : 1.5} />
                </Tooltip>
                <text x={p.x} y={p.y - (nodeR + 10)} textAnchor="middle" fontSize={12} fill={theme.palette.text.secondary}>{name}</text>
              </g>
            );
          })}
        </svg>
      </Box>
    );
  }

  // --------- Entity cards overview ──────────────────────────────────────
  // Each card now answers three questions at a glance, in a tight stack:
  //   1. What is this entity?  (name + 1-line description)
  //   2. What kind is it?      (semantic tag chips: core / pii / etc.)
  //   3. Where does it come from?  (footer: connector icons + table count)
  // We deliberately dropped (a) the relationship-count chip — too detailed
  // for a directory view, and (b) the top-fields mono pills — they didn't
  // help users decide which entity to open and just added noise.
  function EntityCards({
    entities,
    onEntityClick,
    sourceGroupsByEntity,
  }: {
    entities: any[];
    onEntityClick?: (index: number, entity: any) => void;
    // Map of entityName -> one group per connector that contributes data.
    // Each group carries the connector's type, name, and the tables it sources.
    sourceGroupsByEntity?: Record<string, Array<{ connectorId: string; type: string; name: string; tables: string[] }>>;
  }) {
    const ents = Array.isArray(entities) ? entities : [];
    // Pin Unassigned entity to the top
    const sorted = [...ents].sort((a, b) => {
      const aU = Array.isArray(a?.tags) && a.tags.some((t: string) => t.toLowerCase() === 'unassigned');
      const bU = Array.isArray(b?.tags) && b.tags.some((t: string) => t.toLowerCase() === 'unassigned');
      if (aU && !bU) return -1;
      if (!aU && bU) return 1;
      return 0;
    });
    const theme = useTheme();
    return (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
        gap: 1.5,
        width: '100%',
        minWidth: 0,
      }}>
        {sorted.map((e: any, idx: number) => {
          const fields = Array.isArray(e?.fields) ? e.fields : [];
          const tags = Array.isArray(e?.tags) ? e.tags : [];
          const mappings = fields.reduce((acc: number, f: any) => acc + ((Array.isArray(f?.mappings) ? f.mappings.length : 0) || 0), 0);
          const isClickable = !!onEntityClick;
          const isUnassigned = Array.isArray(tags) && tags.some((t: string) => t.toLowerCase() === 'unassigned');
          const sourceGroups = sourceGroupsByEntity?.[String(e?.name || '')];

          // Pre-compute the "what kind is it" chip list so we can sort risk
          // tags (PII) ahead of the others — risk should always read first.
          const tagChips = tags
            .map((raw: string) => ({ raw, tone: classifyTag(raw) }))
            .filter((t: { tone: TagTone }) => t.tone !== 'unassigned')
            .sort((a: any, b: any) => {
              const order: Record<TagTone, number> = { pii: 0, core: 1, transactional: 2, reference: 3, neutral: 4, unassigned: 5 };
              return order[a.tone as TagTone] - order[b.tone as TagTone];
            })
            .slice(0, 4);

          const card = (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                p: 1.5,
                minWidth: 0,
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                ...(isUnassigned && {
                  borderColor: theme.palette.warning.main,
                  borderWidth: 2,
                  gridColumn: { sm: '1 / -1' },
                }),
                ...(isClickable && {
                  cursor: 'pointer',
                  transition: 'border-color .15s ease, box-shadow .15s ease, transform .15s ease',
                  '&:hover': {
                    borderColor: isUnassigned ? theme.palette.warning.dark : theme.palette.primary.main,
                    boxShadow: theme.shadows[3],
                    transform: 'translateY(-1px)',
                  }
                })
              }}
              onClick={() => isClickable && onEntityClick(idx, e)}
            >
              <Stack spacing={1} sx={{ flex: 1 }}>
                {/* Header */}
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    {isUnassigned && <WarningRounded sx={{ fontSize: '1rem', color: 'warning.main', flexShrink: 0 }} />}
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ lineHeight: 1.2, wordBreak: 'break-word', fontSize: '0.9375rem', flex: 1, minWidth: 0 }}
                    >
                      {e?.name || 'Entity'}
                    </Typography>
                    {isUnassigned && (
                      <Chip label="Needs assignment" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600, flexShrink: 0 }} />
                    )}
                  </Stack>
                  {e?.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, fontSize: '0.75rem', lineHeight: 1.4 }}
                      noWrap
                      title={e.description}
                    >
                      {e.description}
                    </Typography>
                  )}
                </Box>

                {/* Tag chips — semantic colors */}
                {tagChips.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {tagChips.map(({ raw, tone }: { raw: string; tone: TagTone }, i: number) => {
                      const props = tagChipProps(tone);
                      return (
                        <Chip
                          key={i}
                          size="small"
                          label={raw}
                          color={props.color}
                          icon={props.icon}
                          variant={tone === 'pii' || tone === 'core' || tone === 'transactional' ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.6875rem', height: 20, fontWeight: 600 }}
                        />
                      );
                    })}
                    {tags.length > tagChips.length && (
                      <Chip size="small" variant="outlined" label={`+${tags.length - tagChips.length}`} sx={{ fontSize: '0.6875rem', height: 20 }} />
                    )}
                  </Stack>
                )}

                {/* Field count — quiet single line */}
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {isUnassigned ? (
                    <>
                      <Chip size="small" variant="outlined" icon={<StorageRounded sx={{ fontSize: 12 }} />} label={`${fields.length} sources`} sx={{ fontSize: '0.6875rem', height: 20 }} />
                      <Chip size="small" variant="outlined" icon={<LayersRounded sx={{ fontSize: 12 }} />} label={`${mappings} fields`} sx={{ fontSize: '0.6875rem', height: 20 }} />
                    </>
                  ) : (
                    <Typography variant="caption" sx={(t) => ({ fontSize: '0.6875rem', color: t.tokens.text.tertiary, fontWeight: 500 })}>
                      <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>{fields.length}</strong> {fields.length === 1 ? 'field' : 'fields'}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              {/* ── Footer: source connectors ──────────────────────────────
                  Just brand icons — no inline labels — so the card stays
                  compact and the visual rhythm doesn't compete with the
                  entity name. Each icon's tooltip carries the full detail:
                  pretty type, connector name, and the source tables it feeds. */}
              {!isUnassigned && sourceGroups && sourceGroups.length > 0 && (
                <Box
                  sx={(t) => ({
                    mt: 1.25,
                    pt: 1,
                    borderTop: `1px solid ${t.tokens.border.subtle}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0,
                  })}
                >
                  {sourceGroups.slice(0, 4).map((g) => {
                    const tablesPreview = g.tables.slice(0, 3).join(', ');
                    const tablesSuffix = g.tables.length > 3 ? ` +${g.tables.length - 3} more` : '';
                    return (
                      <Tooltip
                        key={g.connectorId}
                        placement="top"
                        title={
                          <Box sx={{ py: 0.25 }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.3 }}>
                              {prettyConnectorType(g.type)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6875rem', opacity: 0.85, lineHeight: 1.3 }}>
                              {g.name}
                            </Typography>
                            {g.tables.length > 0 && (
                              <Typography
                                sx={{
                                  fontSize: '0.6875rem',
                                  opacity: 0.7,
                                  lineHeight: 1.3,
                                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                  mt: 0.5,
                                }}
                              >
                                {tablesPreview}{tablesSuffix}
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        <Box
                          sx={(t) => ({
                            width: 22,
                            height: 22,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: `${t.ds.radius.xs}px`,
                            bgcolor: t.tokens.surface.sunken,
                            border: `1px solid ${t.tokens.border.subtle}`,
                            cursor: 'help',
                            transition: 'transform .12s ease, border-color .12s ease',
                            '& > svg': { width: 14, height: 14 },
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              borderColor: t.tokens.border.default,
                            },
                          })}
                        >
                          <ConnectorIcon type={g.type} size={14} />
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {sourceGroups.length > 4 && (
                    <Tooltip
                      placement="top"
                      title={
                        <Box sx={{ py: 0.25 }}>
                          {sourceGroups.slice(4).map((g) => (
                            <Typography key={g.connectorId} sx={{ fontSize: '0.6875rem', lineHeight: 1.4 }}>
                              {g.name} <span style={{ opacity: 0.6 }}>· {prettyConnectorType(g.type)}</span>
                            </Typography>
                          ))}
                        </Box>
                      }
                    >
                      <Typography
                        variant="caption"
                        sx={(t) => ({
                          fontSize: '0.6875rem',
                          color: t.tokens.text.tertiary,
                          fontWeight: 600,
                          ml: 0.25,
                          cursor: 'help',
                        })}
                      >
                        +{sourceGroups.length - 4}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Paper>
          );
          // Tooltip-wrap when clickable so the affordance is discoverable
          // without a redundant body-copy hint above the grid.
          return isClickable ? (
            <Tooltip key={idx} title={`Open ${e?.name || 'entity'} details`} placement="top" enterDelay={400}>
              {card}
            </Tooltip>
          ) : card;
        })}
      </Box>
    );
  }


  // Extract a numeric confidence value. If it's an array or object, pick the first numeric.
  function getFirstConfidenceValue(conf: any): number | null {
    const extract = (v: any): number | null => {
      if (typeof v === 'number' && !Number.isNaN(v)) return v;
      if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
      if (v && typeof v === 'object') {
        if (typeof v.value === 'number') return v.value;
        if (typeof v.score === 'number') return v.score;
        if (typeof v.confidence === 'number') return v.confidence;
      }
      return null;
    };
    if (Array.isArray(conf)) {
      for (const item of conf) {
        const num = extract(item);
        if (num !== null) return num;
      }
      return null;
    }
    return extract(conf);
  }

  // For a field, prefer confidence from the first mapping; fallback to field-level confidence
  function getFieldConfidence(field: any): number | null {
    const mappings = Array.isArray(field?.mappings) ? field.mappings : [];
    if (mappings.length > 0) {
      const mc = mappings[0]?.confidence;
      const v = getFirstConfidenceValue(mc);
      if (typeof v === 'number') return v;
    }
    return getFirstConfidenceValue(field?.confidence);
  }

  // Format confidence value as percentage (0-1 scale to 0-100%)
  function formatConfidenceAsPercent(conf: number | null): string {
    if (conf === null || typeof conf !== 'number') return '-';
    return `${Math.round(conf * 100)}%`;
  }


  React.useEffect(() => {
    const tStr = window.localStorage.getItem('klaris_tenant');
    if (!tStr) return;
    const t = JSON.parse(tStr);
    setTenantId(t.tenant_id);
    if (t.name) setTenantName(t.name);
  }, []);

  React.useEffect(() => {
    async function load() {
      if (!tenantId) return;
      setLoading(true); setError(null);
      try {
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors`), { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) throw new Error('Failed to load connectors');
        const result = await resp.json();
        // We keep the full list (not just active) because the data model is a
        // historical artifact: mappings may reference connectors that are
        // currently paused or errored, and we still need to resolve their
        // type so the entity card footers can render the right brand icon.
        const list = result.connectors || [];
        setConnectors(list);
        // Don't auto-select - let user choose
        setSelectedConnectorIds([]);
      } catch (e: any) {
        setError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [tenantId]);

  const runEnrich = async () => {
    if (!tenantId || selectedConnectorIds.length === 0) return;
    
    // Don't set reviewLoading - we want background processing
    setReviewError(null);
    
    // Immediately show enrichment status banner
    const status = {
      enriching: true,
      reviewId: null,
      startedAt: Date.now(),
      expectedVersion: savedCanonicalVersion || 0
    };
    window.localStorage.setItem('klaris_enrichment_status', JSON.stringify(status));
    setEnrichmentStatus(status);
    
    // Start polling immediately
    startPolling();
    
    // Fire enrichment request in background (don't await)
    const token = window.localStorage.getItem('klaris_jwt');
    const body = { 
      connector_ids: selectedConnectorIds, 
      options: { confidence_threshold: 0.6 },
      domain_context: domainContext.trim() || undefined
    };
    
    // Using NEW hierarchical ontology API for scalability (handles 1000+ tables, 10K+ fields)
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/ontology/reviews`), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(resp => resp.json())
      .then(result => {
        // Update status with review ID
        const updatedStatus = {
          ...status,
          reviewId: result.review_id
        };
        window.localStorage.setItem('klaris_enrichment_status', JSON.stringify(updatedStatus));
        setEnrichmentStatus(updatedStatus);
      })
      .catch(e => {
        setReviewError(e.message || 'Failed to start enrichment');
        window.localStorage.removeItem('klaris_enrichment_status');
        setEnrichmentStatus(null);
        if (pollingInterval) clearInterval(pollingInterval);
      });
  };

  // Simplified UI: no inline schema preview

  const saveGlobalCanonical = async () => {
    if (!tenantId || !reviewData) return;
    setCanonicalSaving(true); setCanonicalMessage(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const payload = {
        base_schema_ids: (reviewData?.input_snapshot?.schema_ids) || [],
        review_id: reviewData.review_id,
        user_edits: reviewData.suggestions || {},
        expected_version: savedCanonicalVersion ?? undefined,
      };
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical`), {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const result = await resp.json();
      if (resp.status === 409) {
        const msg = typeof result?.detail === 'string' ? result.detail : (result?.detail?.message || 'Version conflict');
        setCanonicalMessage(`${msg}. Reloading latest...`);
        // Reload latest and show it
        try {
          const latestResp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), { headers: { Authorization: `Bearer ${token}` } });
          if (latestResp.ok) {
            const latest = await latestResp.json();
            setSavedCanonical(latest?.canonical_graph || null);
            setSavedCanonicalVersion(latest?.version ?? null);
            setSavedCanonicalUpdatedAt(latest?.updated_at || latest?.created_at || latest?.saved_at || null);
          }
        } catch {}
        return;
      }
      if (!resp.ok) throw new Error(result?.detail || 'Save failed');
      setCanonicalMessage(`Global canonical v${result.version} saved`);
      setSavedCanonical(result?.canonical_graph || null);
      setSavedCanonicalVersion(result?.version ?? null);
      setSavedCanonicalUpdatedAt(result?.updated_at || result?.created_at || result?.saved_at || new Date().toISOString());
    } catch (e: any) {
      setCanonicalMessage(e.message || 'Failed to save');
    } finally {
      setCanonicalSaving(false);
    }
  };

  const buildIndex = async () => {
    if (!tenantId) return;
    setIndexBuildLoading(true); setIndexBuildError(null); setIndexBuildCounts(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/index/build`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result?.detail || 'Index build failed');
      setIndexBuildCounts(result?.counts || null);
      notify('Vectors rebuilt successfully', 'success');
    } catch (e: any) {
      setIndexBuildError(e.message || 'Index build failed');
      notify('Failed to rebuild vectors', 'error');
    } finally {
      setIndexBuildLoading(false);
    }
  };

  // refreshIndexStats and doSearch removed - search UI removed

  // Polling function to check for enrichment completion
  const startPolling = () => {
    if (pollingInterval) clearInterval(pollingInterval);
    
    const interval = setInterval(async () => {
      if (!tenantId) return;
      
      try {
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (resp.ok) {
          const result = await resp.json();
          const newVersion = result?.version ?? null;
          
          // Check if version has increased
          const statusStr = window.localStorage.getItem('klaris_enrichment_status');
          if (statusStr) {
            const status = JSON.parse(statusStr);
            if (newVersion && newVersion > status.expectedVersion) {
              // Enrichment complete!
              window.localStorage.removeItem('klaris_enrichment_status');
              setEnrichmentStatus(null);
              clearInterval(interval);
              
              // Update saved canonical
              setSavedCanonical(result?.canonical_graph || null);
              setSavedCanonicalVersion(newVersion);
              setSavedCanonicalUpdatedAt(result?.updated_at || result?.created_at || result?.saved_at || new Date().toISOString());
              setSavedBaseSchemaIds(result?.base_schema_ids || []);
              
              // Show success message
              notify('Enrichment completed successfully!', 'success');
            }
          }
        }
      } catch (e) {
        // Ignore polling errors
      }
    }, 30000); // Poll every 30 seconds
    
    setPollingInterval(interval);
  };
  
  // Load latest saved canonical on mount/tenant change
  React.useEffect(() => {
    (async () => {
      if (!tenantId) return;
      try {
        setSavedCanonicalLoading(true);
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!resp.ok) {
          // 404 means none saved yet - not an error
          setSavedCanonical(null);
          setSavedCanonicalVersion(null);
          setSavedCanonicalUpdatedAt(null);
          setSavedBaseSchemaIds(null);
          return;
        }
        const result = await resp.json();
        setSavedCanonical(result?.canonical_graph || null);
        setSavedCanonicalVersion(result?.version ?? null);
        setSavedCanonicalUpdatedAt(result?.updated_at || result?.created_at || result?.saved_at || null);
        setSavedBaseSchemaIds(result?.base_schema_ids || []);
        // Load domain context if available
        setDomainContext(result?.domain_context || '');
      } catch {
        // Non-fatal in UI
      } finally {
        setSavedCanonicalLoading(false);
      }
    })();
  }, [tenantId]);
  
  // Check for existing enrichment status on mount
  React.useEffect(() => {
    const statusStr = window.localStorage.getItem('klaris_enrichment_status');
    if (statusStr) {
      try {
        const status = JSON.parse(statusStr);
        setEnrichmentStatus(status);
        startPolling();
      } catch {}
    }
    
    // Cleanup polling on unmount
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [tenantId]);

  // Begin edit flow: copy saved to draft
  const beginEdit = () => {
    if (!savedCanonical) return;
    setDraftCanonical(JSON.parse(JSON.stringify(savedCanonical)));
  };

  const discardEdit = () => {
    setDraftCanonical(null);
  };

  // Open entity edit modal
  const openEntityModal = (index: number, entity: any) => {
    setSelectedEntityIndex(index);
    setModalEntityData(JSON.parse(JSON.stringify(entity))); // Deep copy
    setEntityModalOpen(true);
  };

  // Close entity edit modal
  const closeEntityModal = () => {
    setEntityModalOpen(false);
    setSelectedEntityIndex(null);
    setModalEntityData(null);
  };

  // Open relationship edit modal
  const openRelationshipModal = (index: number, relationship: any) => {
    setSelectedRelationshipIndex(index);
    setModalRelationshipData(JSON.parse(JSON.stringify(relationship))); // Deep copy
    setRelationshipModalOpen(true);
    setIsAddingRelationship(false);
  };

  // Open relationship add modal. Optional `fromEntity` arg supports the
  // deep-link from EntityDetailPage's empty-state CTA — we pre-fill the
  // "From entity" field so the user doesn't have to re-enter the entity
  // they were just looking at.
  const openAddRelationshipModal = React.useCallback((fromEntity?: string) => {
    setSelectedRelationshipIndex(null);
    setModalRelationshipData({
      from_entity: fromEntity || '',
      to_entity: '',
      type: 'one_to_many',
      join_on: [],
      confidence: 1.0
    });
    setRelationshipModalOpen(true);
    setIsAddingRelationship(true);
  }, []);

  // Deep-link handler: when arrived here via
  //   /data-model?addRelationshipFor=<entityName>
  // (typically from the EntityDetailPage empty state), wait until the
  // canonical graph has loaded and then auto-open the Add Relationship
  // modal pre-filled with that entity. We strip the query param after
  // firing so a refresh doesn't reopen the modal, and so back-navigation
  // doesn't surprise the user with a stale dialog.
  const autoOpenedAddForRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (savedCanonicalLoading) return;
    if (!savedCanonical) return;
    const params = new URLSearchParams(location.search);
    const target = params.get('addRelationshipFor');
    if (!target) return;
    if (autoOpenedAddForRef.current === target) return;

    // Only pre-fill when the entity actually exists in the canonical
    // graph — otherwise the autocomplete value would be a free-typed
    // string the user couldn't easily fix from a fresh modal.
    const exists = (savedCanonical?.entities || []).some(
      (e: any) => String(e?.name || '') === target,
    );
    autoOpenedAddForRef.current = target;
    openAddRelationshipModal(exists ? target : undefined);

    params.delete('addRelationshipFor');
    const next = params.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : '' },
      { replace: true },
    );
  }, [
    location.search,
    location.pathname,
    savedCanonical,
    savedCanonicalLoading,
    openAddRelationshipModal,
    navigate,
  ]);

  // Close relationship modal
  const closeRelationshipModal = () => {
    setRelationshipModalOpen(false);
    setSelectedRelationshipIndex(null);
    setModalRelationshipData(null);
    setIsAddingRelationship(false);
  };

  // Delete relationship (called from modal)
  const deleteRelationship = async () => {
    if (!tenantId || selectedRelationshipIndex === null) return;
    
    if (!window.confirm('Are you sure you want to delete this relationship?')) return;

    try {
      const token = window.localStorage.getItem('klaris_jwt');
      
      // Get relationship data to identify it
      const relationships = savedCanonical?.relationships || [];
      const relationship = relationships[selectedRelationshipIndex];
      if (!relationship) {
        alert('Relationship not found');
        return;
      }
      
      // Use PATCH endpoint with relationship identifiers (immune to filtering/sorting)
      const payload = {
        from_entity: relationship.from_entity,
        to_entity: relationship.to_entity,
        relationship_type: relationship.relationship_type || relationship.type,
        operation: 'delete',
        expected_version: savedCanonicalVersion ?? undefined,
      };
      
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/relationship`), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const result = await resp.json();
      
      if (resp.status === 409) {
        alert('Version conflict. Please close and retry.');
        return;
      }
      
      if (!resp.ok) throw new Error(result?.detail || 'Delete failed');
      
      setSavedCanonical(result?.canonical_graph || null);
      setSavedCanonicalVersion(result?.version ?? null);
      setSavedCanonicalUpdatedAt(result?.created_at || new Date().toISOString());
      closeRelationshipModal();
    } catch (e: any) {
      alert(e.message || 'Failed to delete relationship');
    }
  };


  // Export functions
  const exportEntities = async () => {
    if (!tenantId) return;
    setExportLoading(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const response = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/export/entities`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Build filename: {tenantName}_entities_v{version}.csv
        let filename = 'entities';
        if (tenantName) {
          // Sanitize tenant name for filename (replace spaces and special chars with underscore)
          const sanitizedName = tenantName.replace(/[^a-zA-Z0-9]/g, '_');
          filename = `${sanitizedName}_${filename}`;
        }
        if (savedCanonicalVersion) {
          filename = `${filename}_v${savedCanonicalVersion}`;
        }
        a.download = `${filename}.csv`;
        
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Export failed:', response.statusText);
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };


  function EntitiesTable({ entities }: { entities: any[] }) {
    return (
      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell>Fields</TableCell>
            <TableCell>Mappings</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(entities || []).map((e, idx) => (
            <TableRow key={idx}>
              <TableCell>{e.name}</TableCell>
              <TableCell>{(e.tags || []).join(', ')}</TableCell>
              <TableCell>{(e.fields || []).map((f: any) => f.name).slice(0, 6).join(', ')}{(e.fields || []).length > 6 ? '…' : ''}</TableCell>
              <TableCell>{(e.fields || []).reduce((acc: number, f: any) => acc + ((f.mappings || []).length || 0), 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // ────────── Relationships v2 — readable, grouped, mobile-friendly ──────
  // Each row reads like a sentence:
  //   Order ───[many-to-one]──▶ Customer    on order.customer_id = customer.id    92%
  // Rows are grouped by the *from* entity. Groups are collapsible so a
  // 200-relationship model doesn't drown the user in a 200-row table.
  function RelationshipsListV2({
    rels,
    onRelationshipClick,
    isMobile,
    collapsedGroups,
    onToggleGroup,
  }: {
    rels: any[];
    onRelationshipClick?: (index: number, rel: any) => void;
    isMobile: boolean;
    collapsedGroups: Record<string, boolean>;
    onToggleGroup: (key: string) => void;
  }) {
    const theme = useTheme();
    const isClickable = !!onRelationshipClick;
    const flat = (rels || []).map((r: any, idx: number) => ({ ...r, originalIndex: idx }));

    if (flat.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}>
          <LinkRounded sx={(t) => ({ fontSize: 28, color: t.tokens.text.tertiary, mb: 0.5 })} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No relationships yet for the current view.
          </Typography>
        </Paper>
      );
    }

    // Group by source entity for visual organisation
    const groups: Record<string, any[]> = {};
    flat.forEach((r: any) => {
      const k = r.from_entity || '(unknown)';
      (groups[k] = groups[k] || []).push(r);
    });
    const keys = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    // Confidence helper — 90+ green, 70+ amber, otherwise red
    const confTone = (n: number | null): { bg: string; fg: string; label: string } => {
      const pct = typeof n === 'number' ? Math.round(n * 100) : null;
      if (pct === null) return { bg: alpha(theme.palette.text.primary, 0.08), fg: theme.palette.text.secondary, label: '—' };
      if (pct >= 90) return { bg: alpha(theme.palette.success.main, 0.12), fg: theme.palette.success.main, label: `${pct}%` };
      if (pct >= 70) return { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.main, label: `${pct}%` };
      return { bg: alpha(theme.palette.error.main, 0.12), fg: theme.palette.error.main, label: `${pct}%` };
    };

    return (
      <Stack spacing={1.5}>
        {keys.map((from) => {
          const rows = groups[from];
          const isCollapsed = !!collapsedGroups[from];
          return (
            <Box key={from}>
              {/* Group header — click to collapse/expand */}
              <Box
                onClick={() => onToggleGroup(from)}
                role="button"
                aria-expanded={!isCollapsed}
                sx={(t) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.75,
                  borderRadius: `${t.ds.radius.sm}px`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: `background-color ${t.ds.motion.duration.fast}ms`,
                  '&:hover': { bgcolor: t.tokens.surface.sunken },
                })}
              >
                {isCollapsed
                  ? <ExpandMoreRounded sx={{ fontSize: 18, transform: 'rotate(-90deg)' }} />
                  : <ExpandMoreRounded sx={{ fontSize: 18 }} />
                }
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>
                  {from}
                </Typography>
                <Chip
                  size="small"
                  label={rows.length}
                  variant="outlined"
                  sx={{ fontSize: '0.6875rem', height: 18 }}
                />
              </Box>

              <Collapse in={!isCollapsed} timeout={150} unmountOnExit>
                <Stack
                  spacing={0.5}
                  sx={(t) => ({
                    mt: 0.5,
                    borderLeft: `2px solid ${t.tokens.border.subtle}`,
                    ml: 1.25,
                    pl: 1.25,
                  })}
                >
                  {rows.map((r: any, idx: number) => {
                    const conf = getFirstConfidenceValue(r.confidence);
                    const tone = confTone(conf);
                    const joins = Array.isArray(r.join_on) ? r.join_on : [];
                    const joinTexts = joins.map((p: any) => `${r.from_entity}.${p.from_field} = ${r.to_entity}.${p.to_field}`);
                    const onFieldShort = joins.length === 1
                      ? `${r.from_entity}.${joins[0]?.from_field || '?'}`
                      : (joins.length > 1 ? `${joins.length} join keys` : '');

                    if (isMobile) {
                      // Stacked card layout for narrow viewports — same info,
                      // arranged top-to-bottom so nothing overflows.
                      return (
                        <Paper
                          key={`${from}-${idx}`}
                          variant="outlined"
                          onClick={() => isClickable && onRelationshipClick?.(r.originalIndex, r)}
                          sx={(t) => ({
                            p: 1.25,
                            borderRadius: `${t.ds.radius.sm}px`,
                            ...(isClickable && {
                              cursor: 'pointer',
                              transition: `border-color ${t.ds.motion.duration.fast}ms, background-color ${t.ds.motion.duration.fast}ms`,
                              '&:hover': { borderColor: t.tokens.brand.primary, bgcolor: t.tokens.surface.sunken },
                            }),
                          })}
                        >
                          <Stack spacing={0.75}>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>{r.from_entity}</Typography>
                              <ArrowForwardRounded sx={(t) => ({ fontSize: 14, color: t.tokens.text.tertiary })} />
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8125rem' }}>{r.to_entity}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip size="small" label={r.type || '—'} variant="outlined" sx={{ fontSize: '0.6875rem', height: 20, fontWeight: 600 }} />
                              <Box
                                sx={{
                                  px: 0.75, height: 20, display: 'inline-flex', alignItems: 'center',
                                  borderRadius: 999, bgcolor: tone.bg, color: tone.fg,
                                  fontSize: '0.6875rem', fontWeight: 700,
                                }}
                              >
                                {tone.label}
                              </Box>
                            </Stack>
                            {joinTexts.length > 0 && (
                              <Typography
                                variant="caption"
                                sx={(t) => ({
                                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                  fontSize: '0.6875rem',
                                  color: t.tokens.text.tertiary,
                                  wordBreak: 'break-word',
                                })}
                              >
                                {joinTexts.join('  ·  ')}
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      );
                    }

                    // Desktop / tablet inline row layout
                    return (
                      <Box
                        key={`${from}-${idx}`}
                        onClick={() => isClickable && onRelationshipClick?.(r.originalIndex, r)}
                        sx={(t) => ({
                          display: 'grid',
                          gridTemplateColumns: 'minmax(120px, 1fr) auto minmax(120px, 1fr) auto minmax(180px, 2fr) auto',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.25,
                          py: 1,
                          borderRadius: `${t.ds.radius.sm}px`,
                          border: `1px solid ${t.tokens.border.subtle}`,
                          bgcolor: 'background.paper',
                          ...(isClickable && {
                            cursor: 'pointer',
                            transition: `border-color ${t.ds.motion.duration.fast}ms, background-color ${t.ds.motion.duration.fast}ms`,
                            '&:hover': { borderColor: t.tokens.brand.primary, bgcolor: t.tokens.surface.sunken },
                          }),
                        })}
                      >
                        {/* From */}
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8125rem' }} title={r.from_entity}>
                          {r.from_entity}
                        </Typography>

                        {/* Arrow + cardinality */}
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={(t) => ({ color: t.tokens.text.tertiary })}>
                          <Box
                            sx={(t) => ({
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              color: t.tokens.text.secondary,
                              px: 0.625,
                              py: 0.125,
                              border: `1px solid ${t.tokens.border.default}`,
                              borderRadius: `${t.ds.radius.xs}px`,
                              bgcolor: t.tokens.surface.sunken,
                              whiteSpace: 'nowrap',
                            })}
                          >
                            {r.type || '—'}
                          </Box>
                          <ArrowForwardRounded sx={{ fontSize: 14 }} />
                        </Stack>

                        {/* To */}
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8125rem' }} title={r.to_entity}>
                          {r.to_entity}
                        </Typography>

                        {/* "on" label */}
                        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, fontSize: '0.6875rem' })}>
                          on
                        </Typography>

                        {/* Join field(s), monospaced */}
                        <Tooltip title={joinTexts.join('\n')} placement="top">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={(t) => ({
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              fontSize: '0.75rem',
                              color: t.tokens.text.secondary,
                              minWidth: 0,
                            })}
                          >
                            {onFieldShort || '—'}
                          </Typography>
                        </Tooltip>

                        {/* Confidence pill — color-tiered */}
                        <Box
                          sx={{
                            px: 0.875,
                            height: 22,
                            display: 'inline-flex',
                            alignItems: 'center',
                            borderRadius: 999,
                            bgcolor: tone.bg,
                            color: tone.fg,
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            justifySelf: 'end',
                          }}
                        >
                          {tone.label}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    );
  }

  // ---------- Editable components ----------
  // Relationship Edit Modal Component - COMPLETELY ISOLATED, ZERO PARENT STATE UPDATES
  function RelationshipEditModal() {
    // ALL STATE IS LOCAL - nothing touches parent during editing/saving
    const [localData, setLocalData] = React.useState<any>(null);
    const [newJoinField, setNewJoinField] = React.useState({ from_field: '', to_field: '' });
    const [saving, setSaving] = React.useState(false);
    const [saveMessage, setSaveMessage] = React.useState<string | null>(null);
    // Snapshot entities to avoid re-renders when savedCanonical changes
    const [snapshotEntities, setSnapshotEntities] = React.useState<any[]>([]);
    
    // Snapshot ALL data when modal opens
    React.useEffect(() => {
      if (relationshipModalOpen && modalRelationshipData) {
        setLocalData(JSON.parse(JSON.stringify(modalRelationshipData)));
        setNewJoinField({ from_field: '', to_field: '' });
        setSaving(false);
        setSaveMessage(null);
        // Snapshot entities once
        setSnapshotEntities(JSON.parse(JSON.stringify(savedCanonical?.entities || [])));
      }
    }, [relationshipModalOpen]);

    // Available entities - from snapshot, not savedCanonical!
    const availableEntities = React.useMemo(() => {
      return snapshotEntities.map((e: any) => e.name);
    }, [snapshotEntities]);

    // From/To fields - from snapshot, not savedCanonical!
    const fromFields = React.useMemo(() => {
      if (!localData?.from_entity) return [];
      const entity = snapshotEntities.find((e: any) => e.name === localData.from_entity);
      return Array.from(new Set((entity?.fields || []).map((f: any) => String(f.name || '')))).filter(Boolean);
    }, [localData?.from_entity, snapshotEntities]);

    const toFields = React.useMemo(() => {
      if (!localData?.to_entity) return [];
      const entity = snapshotEntities.find((e: any) => e.name === localData.to_entity);
      return Array.from(new Set((entity?.fields || []).map((f: any) => String(f.name || '')))).filter(Boolean);
    }, [localData?.to_entity, snapshotEntities]);

    if (!relationshipModalOpen || !localData) return null;

    // Handle save - close modal BEFORE updating parent to avoid flicker
    const handleSave = async () => {
      if (!tenantId || !localData) return;
      
      // Validation
      if (!localData.from_entity || !localData.to_entity) {
        notify('From Entity and To Entity are required', 'warning');
        return;
      }

      setSaving(true);
      setSaveMessage(null);
      
      try {
        const token = window.localStorage.getItem('klaris_jwt');

        // Include any pending join pair if both ends selected but not added yet
        const currentPairs = Array.isArray(localData.join_on) ? [...localData.join_on] : [];
        if ((newJoinField.from_field || '').trim() && (newJoinField.to_field || '').trim()) {
          const exists = currentPairs.some((p: any) => String(p.from_field) === newJoinField.from_field && String(p.to_field) === newJoinField.to_field);
          if (!exists) {
            currentPairs.push({ from_field: newJoinField.from_field, to_field: newJoinField.to_field });
          }
        }
        const relToSave = { ...localData, join_on: currentPairs };
        
        // Use PATCH endpoint to avoid sending entire canonical graph
        // For update: use relationship identifiers (immune to filtering/sorting)
        // For add: no identifiers needed
        const payload: any = {
          relationship_data: relToSave,
          operation: isAddingRelationship ? 'add' : 'update',
          expected_version: savedCanonicalVersion ?? undefined,
        };
        
        // For update, get the original relationship to identify it
        if (!isAddingRelationship && selectedRelationshipIndex !== null) {
          const relationships = savedCanonical?.relationships || [];
          const originalRelationship = relationships[selectedRelationshipIndex];
          if (!originalRelationship) {
            notify('Original relationship not found', 'error');
            setSaving(false);
            return;
          }
          payload.from_entity = originalRelationship.from_entity;
          payload.to_entity = originalRelationship.to_entity;
          payload.relationship_type = originalRelationship.relationship_type || originalRelationship.type;
        }
        
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/relationship`), {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        const result = await resp.json();
        
        if (resp.status === 409) {
          notify('Version conflict. Please close and retry.', 'error');
          // Reload latest in background
          try {
            const latestResp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (latestResp.ok) {
              const latest = await latestResp.json();
              setSavedCanonical(latest?.canonical_graph || null);
              setSavedCanonicalVersion(latest?.version ?? null);
              setSavedCanonicalUpdatedAt(latest?.created_at || null);
            }
          } catch {}
          setSaving(false);
          return;
        }
        
        if (!resp.ok) throw new Error(result?.detail || 'Save failed');
        // Close first to avoid any visual flicker while parent updates
        closeRelationshipModal();
        // Now refresh parent state in the background
        try {
          const latestResp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (latestResp.ok) {
            const latest = await latestResp.json();
            setSavedCanonical(latest?.canonical_graph || result?.canonical_graph || null);
            setSavedCanonicalVersion(latest?.version ?? result?.version ?? null);
            setSavedCanonicalUpdatedAt(latest?.created_at || result?.created_at || new Date().toISOString());
          } else {
            setSavedCanonical(result?.canonical_graph || null);
            setSavedCanonicalVersion(result?.version ?? null);
            setSavedCanonicalUpdatedAt(result?.created_at || new Date().toISOString());
          }
        } catch {
          setSavedCanonical(result?.canonical_graph || null);
          setSavedCanonicalVersion(result?.version ?? null);
          setSavedCanonicalUpdatedAt(result?.created_at || new Date().toISOString());
        }
        
      } catch (e: any) {
        notify(e.message || 'Failed to save relationship', 'error');
        setSaving(false);
      }
    };

    return (
      <AppDialog
        open={relationshipModalOpen}
        onClose={closeRelationshipModal}
        size="md"
        title={isAddingRelationship ? 'Add relationship' : 'Edit relationship'}
        busy={saving}
        dividers
        primaryAction={{ label: saving ? 'Saving…' : 'Save', onClick: handleSave }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: closeRelationshipModal }}
        actionsLeftExtra={
          !isAddingRelationship ? (
            <Button onClick={deleteRelationship} color="error" disabled={saving} sx={{ textTransform: 'none' }}>
              Delete
            </Button>
          ) : undefined
        }
      >
          <Stack spacing={3}>
            {/* From/To Entity Selection */}
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>Entities</Typography>
              <Stack direction="row" spacing={2}>
                <Autocomplete
                  fullWidth
                  options={availableEntities}
                  value={localData.from_entity || ''}
                  onChange={(_, val) => setLocalData({
                    ...localData,
                    from_entity: val || '',
                    // Reset join pairs when entity changes to avoid stale pairs
                    join_on: []
                  })}
                  renderInput={(params) => <TextField {...params} label="From Entity" required />}
                />
                <Autocomplete
                  fullWidth
                  options={availableEntities}
                  value={localData.to_entity || ''}
                  onChange={(_, val) => setLocalData({
                    ...localData,
                    to_entity: val || '',
                    // Reset join pairs when entity changes to avoid stale pairs
                    join_on: []
                  })}
                  renderInput={(params) => <TextField {...params} label="To Entity" required />}
                />
              </Stack>
            </Box>

            {/* Relationship Type */}
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>Relationship Type</Typography>
              <Select
                fullWidth
                value={localData.type || 'one_to_many'}
                onChange={(e) => setLocalData({...localData, type: e.target.value})}
              >
                <MenuItem value="one_to_one">One to One</MenuItem>
                <MenuItem value="one_to_many">One to Many</MenuItem>
                <MenuItem value="many_to_one">Many to One</MenuItem>
                <MenuItem value="many_to_many">Many to Many</MenuItem>
              </Select>
            </Box>

            {/* Join Fields */}
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Join Fields ({(localData.join_on || []).length})
              </Typography>
              
              {/* Existing Join Fields */}
              {Array.isArray(localData.join_on) && localData.join_on.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {localData.join_on.map((j: any, idx: number) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          <strong>{localData.from_entity}</strong>.{j.from_field} = <strong>{localData.to_entity}</strong>.{j.to_field}
                        </Typography>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => {
                            const updated = localData.join_on.filter((_: any, i: number) => i !== idx);
                            setLocalData({...localData, join_on: updated});
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Add New Join Field */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Add Join Field</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Autocomplete<string, false, false, false>
                    sx={{ flex: 1 }}
                    size="small"
                    options={(fromFields as string[])}
                    value={newJoinField.from_field}
                    onChange={(_, val) => setNewJoinField((prev) => ({...prev, from_field: (val as string) || ''}))}
                    renderInput={(params) => <TextField {...params} label="From Field" />}
                    disabled={!localData.from_entity}
                  />
                  <Typography variant="body2">=</Typography>
                  <Autocomplete<string, false, false, false>
                    sx={{ flex: 1 }}
                    size="small"
                    options={(toFields as string[])}
                    value={newJoinField.to_field}
                    onChange={(_, val) => setNewJoinField((prev) => ({...prev, to_field: (val as string) || ''}))}
                    renderInput={(params) => <TextField {...params} label="To Field" />}
                    disabled={!localData.to_entity}
                  />
                  <Button 
                    size="small" 
                    startIcon={<AddIcon />} 
                    onClick={() => {
                      if (!newJoinField.from_field || !newJoinField.to_field) return;
                      // Normalize join_on to ensure a stable array of pairs
                      const nextPairs = Array.isArray(localData.join_on) ? [...localData.join_on] : [];
                      nextPairs.push({ from_field: newJoinField.from_field, to_field: newJoinField.to_field });
                      setLocalData({ ...localData, join_on: nextPairs });
                      setNewJoinField({ from_field: '', to_field: '' });
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </Paper>
            </Box>

            {/* Confidence (Read-only) */}
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>Confidence</Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'action.hover' }}>
                <Typography variant="h4" fontWeight={600} color="text.secondary">
                  {typeof localData.confidence === 'number' ? formatConfidenceAsPercent(localData.confidence) : '100%'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Read-only
                </Typography>
              </Paper>
            </Box>
          </Stack>
      </AppDialog>
    );
  }

  function EditableEntitiesTable({ entities }: { entities: any[] }) {
    const [newTagInputByIndex, setNewTagInputByIndex] = React.useState<Record<number, string>>({});
    const setNewTagInput = (i: number, v: string) => setNewTagInputByIndex((prev) => ({ ...prev, [i]: v }));
    const onEntityChange = (i: number, patch: any) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        next.entities[i] = { ...(next.entities[i] || {}), ...patch };
        return next;
      });
    };
    const onFieldChange = (i: number, j: number, patch: any) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        next.entities[i].fields[j] = { ...(next.entities[i].fields[j] || {}), ...patch };
        return next;
      });
    };
    const addEntity = () => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || { entities: [] }));
        (next.entities = next.entities || []).push({ name: 'New Entity', description: '', fields: [] });
        return next;
      });
    };
    const removeEntity = (i: number) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        next.entities.splice(i, 1);
        return next;
      });
    };
    const addField = (i: number) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        (next.entities[i].fields = next.entities[i].fields || []).push({ name: 'new_field', description: '', semantic_type: '', pii_sensitivity: 'none', nullable: false, data_type: '' });
        return next;
      });
    };
    const removeField = (i: number, j: number) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        next.entities[i].fields.splice(j, 1);
        return next;
      });
    };
    const addTag = (i: number, tag: string) => {
      const t = (tag || '').trim(); if (!t) return;
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        const tags = (next.entities[i].tags = next.entities[i].tags || []);
        if (!tags.includes(t)) tags.push(t);
        return next;
      });
    };
    const removeTag = (i: number, tag: string) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        next.entities[i].tags = (next.entities[i].tags || []).filter((x: string) => x !== tag);
        return next;
      });
    };

    const headerCellSx = { position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1, fontSize: 12 } as const;
    const colSx = {
      name: { width: 160 },
      desc: { width: 260 },
      semantic: { width: 140 },
      pii: { width: 110 },
      act: { width: 64, textAlign: 'right' },
    } as const;

    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Button startIcon={<AddIcon />} onClick={addEntity}>Add Entity</Button>
        </Stack>
        <Stack direction="column" spacing={1}>
          {(entities || []).map((e, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 2fr', gap: 1 }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <TextField size="small" placeholder="Name" value={e.name || ''} onChange={(ev) => onEntityChange(i, { name: ev.target.value })} sx={{ width: 220 }} />
                    <IconButton onClick={() => removeEntity(i)} aria-label="remove entity"><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                  <TextField size="small" placeholder="Description" value={e.description || ''} onChange={(ev) => onEntityChange(i, { description: ev.target.value })} fullWidth sx={{ mb: 0.5 }} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" placeholder="Add tag" value={newTagInputByIndex[i] || ''} onChange={(ev) => setNewTagInput(i, ev.target.value)} sx={{ width: 220 }} />
                    <Button size="small" onClick={() => { addTag(i, newTagInputByIndex[i] || ''); setNewTagInput(i, ''); }}>Add Tag</Button>
                  </Stack>
                  {(e.tags || []).length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
                      {(e.tags || []).map((t: string, idx: number) => (
                        <Chip key={idx} label={t} size="small" onDelete={() => removeTag(i, t)} sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...headerCellSx, ...colSx.name }}>Name</TableCell>
                          <TableCell sx={{ ...headerCellSx, ...colSx.desc }}>Description</TableCell>
                        <TableCell sx={{ ...headerCellSx, ...colSx.semantic }}>Semantic</TableCell>
                          <TableCell sx={{ ...headerCellSx, ...colSx.pii }}>PII</TableCell>
                          <TableCell sx={{ ...headerCellSx, width: 110 }}>Confidence</TableCell>
                          <TableCell sx={{ ...headerCellSx, ...colSx.act }}>Act</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(e.fields || []).map((f: any, j: number) => (
                          <TableRow key={j}>
                            <TableCell sx={colSx.name}>
                              <Tooltip title={f.name || ''}><TextField size="small" placeholder="name" value={f.name || ''} onChange={(ev) => onFieldChange(i, j, { name: ev.target.value })} /></Tooltip>
                            </TableCell>
                            <TableCell sx={colSx.desc}>
                              <Tooltip title={f.description || ''}><TextField size="small" placeholder="description" value={f.description || ''} onChange={(ev) => onFieldChange(i, j, { description: ev.target.value })} fullWidth /></Tooltip>
                            </TableCell>
                          <TableCell sx={colSx.semantic}><TextField size="small" placeholder="semantic" value={f.semantic_type || ''} onChange={(ev) => onFieldChange(i, j, { semantic_type: ev.target.value })} /></TableCell>
                            <TableCell sx={colSx.pii}>
                              <Select size="small" value={(f.pii_sensitivity || 'none')} onChange={(ev) => onFieldChange(i, j, { pii_sensitivity: ev.target.value })} fullWidth>
                                {['none','low','medium','high'].map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                              </Select>
                            </TableCell>
                            <TableCell sx={{ width: 110 }}>
                              <TextField size="small" type="text" inputProps={{ readOnly: true }} value={(() => { const cv = getFieldConfidence(f); return formatConfidenceAsPercent(cv); })()} />
                            </TableCell>
                          <TableCell sx={colSx.act}><IconButton size="small" color="error" onClick={() => removeField(i, j)} aria-label="remove field"><DeleteIcon fontSize="small" /></IconButton></TableCell>
                          </TableRow>
                        ))}
                      <TableRow>
                        <TableCell colSpan={6}><Button size="small" startIcon={<AddIcon />} onClick={() => addField(i)}>Add Field</Button></TableCell>
                      </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Box>
    );
  }

  // Entity Edit Modal Component - COMPLETELY ISOLATED, ZERO PARENT STATE UPDATES
  function EntityEditModal() {
    // ALL STATE IS LOCAL - nothing touches parent during editing/saving
    const [localData, setLocalData] = React.useState<any>(null);
    const [newTagInput, setNewTagInput] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [saveMessage, setSaveMessage] = React.useState<string | null>(null);
    // Snapshot relationships to avoid re-renders when savedCanonical changes
    const [snapshotRelationships, setSnapshotRelationships] = React.useState<any[]>([]);
    // Snapshot entities for reassignment dropdown
    const [snapshotEntities, setSnapshotEntities] = React.useState<any[]>([]);
    // Track mapping reassignments: "fieldIdx-mappingIdx" -> {targetEntity, newFieldName}
    const [mappingReassignments, setMappingReassignments] = React.useState<Record<string, {targetEntity: string, newFieldName: string}>>({});
    // Track expanded field groups
    const [expandedFields, setExpandedFields] = React.useState<Set<number>>(new Set());
    // NEW: Track selected fields per source for batch operations
    const [selectedFieldsBySource, setSelectedFieldsBySource] = React.useState<Record<string, Set<string>>>({}); // sourceEntity -> Set of "fieldIdx-mappingIdx"
    // NEW: Track target entity per source
    const [targetEntityBySource, setTargetEntityBySource] = React.useState<Record<string, string>>({}); // sourceEntity -> targetEntityName
    
    // Snapshot ALL data when modal opens
    React.useEffect(() => {
      if (entityModalOpen && modalEntityData) {
        setLocalData(JSON.parse(JSON.stringify(modalEntityData)));
        setNewTagInput('');
        setSaving(false);
        setSaveMessage(null);
        setMappingReassignments({});
        setSelectedFieldsBySource({});
        setTargetEntityBySource({});
        // Auto-expand all fields for Unassigned entity
        if (modalEntityData && Array.isArray(modalEntityData?.tags) && modalEntityData.tags.some((t: string) => t.toLowerCase() === 'unassigned')) {
          const allFieldIndices = new Set<number>((modalEntityData.fields || []).map((_: any, idx: number) => idx));
          setExpandedFields(allFieldIndices);
        } else {
          setExpandedFields(new Set<number>());
        }
        // Snapshot relationships once
        setSnapshotRelationships(JSON.parse(JSON.stringify(savedCanonical?.relationships || [])));
        // Snapshot entities for reassignment dropdown
        setSnapshotEntities(JSON.parse(JSON.stringify(savedCanonical?.entities || [])));
      }
    }, [entityModalOpen]);

    // Calculate relationships - from snapshot, not savedCanonical!
    const entityRelationships = React.useMemo(() => {
      if (!localData) return [];
      return snapshotRelationships.filter((r: any) => 
        r.from_entity === localData.name || r.to_entity === localData.name
      );
    }, [localData?.name, snapshotRelationships]);

    // Check if this is the Unassigned entity
    const isUnassignedEntity = React.useMemo(() => {
      return Array.isArray(localData?.tags) && localData.tags.some((t: string) => t.toLowerCase() === 'unassigned');
    }, [localData?.tags]);

    // Group mappings by source entity for Unassigned entity UI
    const mappingsBySource = React.useMemo(() => {
      if (!isUnassignedEntity || !localData) return {};
      
      const groups: Record<string, Array<{fieldIdx: number, mappingIdx: number, field: any, mapping: any}>> = {};
      
      (localData.fields || []).forEach((field: any, fieldIdx: number) => {
        (field.mappings || []).forEach((mapping: any, mappingIdx: number) => {
          const sourceEntity = mapping.source_entity || 'Unknown';
          if (!groups[sourceEntity]) {
            groups[sourceEntity] = [];
          }
          groups[sourceEntity].push({ fieldIdx, mappingIdx, field, mapping });
        });
      });
      
      return groups;
    }, [localData, isUnassignedEntity]);

    // Get list of target entities (excluding Unassigned itself)
    const targetEntities = React.useMemo(() => {
      return snapshotEntities
        .filter((e: any) => {
          const isUnassigned = Array.isArray(e?.tags) && e.tags.some((t: string) => t.toLowerCase() === 'unassigned');
          return !isUnassigned;
        })
        .map((e: any) => e.name)
        .filter(Boolean);
    }, [snapshotEntities]);

    if (!entityModalOpen || !localData) return null;

    // Handle save - close modal BEFORE updating parent to avoid flicker
    const handleSave = async () => {
      if (!tenantId || !localData || selectedEntityIndex === null) return;
      
      // Build movements from batch selection (new UX) or individual reassignments (old fallback)
      const movements: Array<{source_entity_name: string, field_index: number, mapping_indices: number[], target_entity_name: string, new_field_name: string}> = [];
      
      if (isUnassignedEntity) {
        // NEW: Use batch selection by source
        const hasSelections = Object.keys(selectedFieldsBySource).some(src => (selectedFieldsBySource[src]?.size || 0) > 0);
        
        if (hasSelections) {
          // Group selected fields by source and build movements
          for (const [sourceEntity, selectedKeys] of Object.entries(selectedFieldsBySource)) {
            const targetEntity = targetEntityBySource[sourceEntity];
            
            if (!targetEntity || !selectedKeys || selectedKeys.size === 0) {
              continue; // Skip sources without target entity or selections
            }
            
            // Create one movement per mapping (each mapping becomes a separate field)
            selectedKeys.forEach((key: string) => {
              const [fieldIdxStr, mappingIdxStr] = key.split('-');
              const fieldIdx = parseInt(fieldIdxStr);
              const mappingIdx = parseInt(mappingIdxStr);
              
              const field = localData.fields[fieldIdx];
              const mapping = field?.mappings?.[mappingIdx];
              const sourceField = mapping?.source_field || `field_${fieldIdx}_${mappingIdx}`;
              
              movements.push({
                source_entity_name: localData.name,
                field_index: fieldIdx,
                mapping_indices: [mappingIdx], // Single mapping per movement
                target_entity_name: targetEntity,
                new_field_name: sourceField // Use source field name
              });
            });
          }
          
          if (movements.length === 0) {
            notify('Please select fields and target entities to move.', 'warning');
            return;
          }
        }
      }
      
      // Count total mappings being moved (for accurate snackbar message)
      const totalMappingsMoved = movements.reduce((sum, m) => sum + m.mapping_indices.length, 0);
      
      // Validation: Check for duplicate field names in target entities
      if (movements.length > 0) {
        const entitiesByName: Record<string, any> = {};
        snapshotEntities.forEach((e: any) => {
          entitiesByName[e.name] = e;
        });

        for (const movement of movements) {
          const targetEntity = entitiesByName[movement.target_entity_name];
          
          if (targetEntity && movement.new_field_name) {
            const existingFieldNames = (targetEntity.fields || []).map((f: any) => f.name?.toLowerCase());
            if (existingFieldNames.includes(movement.new_field_name.toLowerCase())) {
              notify(`Cannot move field "${movement.new_field_name}" to "${movement.target_entity_name}": A field with this name already exists in the target entity.`, 'error');
              return;
            }
          }
        }
      }

      setSaving(true);
      setSaveMessage(null);
      
      try {
        const token = window.localStorage.getItem('klaris_jwt');
        
        // Use PATCH endpoint for Unassigned entity field movements (avoids sending entire canonical graph)
        if (isUnassignedEntity && movements.length > 0) {
          const payload = {
            movements: movements,
            expected_version: savedCanonicalVersion ?? undefined,
          };
          
          const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/move-fields`), {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          
          const result = await resp.json();
          
          if (resp.status === 409) {
            notify('Version conflict. Please close and retry.', 'error');
            // Reload latest in background
            try {
              const latestResp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (latestResp.ok) {
                const latest = await latestResp.json();
                setSavedCanonical(latest?.canonical_graph || null);
                setSavedCanonicalVersion(latest?.version ?? null);
                setSavedCanonicalUpdatedAt(latest?.created_at || null);
              }
            } catch {}
            setSaving(false);
            return;
          }
          
          if (!resp.ok) throw new Error(result?.detail || 'Save failed');
          
          // Check if any entities were removed (e.g., empty Unassigned entity)
          const entitiesRemoved = result?.entities_removed || [];
          
          // Close first to avoid any visual flicker while parent updates
          closeEntityModal();
          // Now update parent state
          setSavedCanonical(result?.canonical_graph || null);
          setSavedCanonicalVersion(result?.version ?? null);
          setSavedCanonicalUpdatedAt(result?.created_at || new Date().toISOString());
          
          // Show success message with entity removal info if applicable
          if (entitiesRemoved.length > 0) {
            notify(`Moved ${totalMappingsMoved} field(s) successfully. Removed empty entity: ${entitiesRemoved.join(', ')}`, 'success');
          } else {
            notify(`Moved ${totalMappingsMoved} field(s) successfully`, 'success');
          }
          
        } else {
          // Normal save for non-Unassigned entity - use PATCH endpoint to avoid sending entire canonical graph
          // Use entity name as identifier (immune to filtering/sorting)
          const entityName = localData.name;
          if (!entityName) {
            notify('Entity name is required', 'error');
            setSaving(false);
            return;
          }
          
          const payload = {
            entity_name: entityName,
            entity_data: localData,
            expected_version: savedCanonicalVersion ?? undefined,
          };
          
          const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/entity`), {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          
          const result = await resp.json();
          
          if (resp.status === 409) {
            notify('Version conflict. Please close and retry.', 'error');
            // Reload latest in background
            try {
              const latestResp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest`), {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (latestResp.ok) {
                const latest = await latestResp.json();
                setSavedCanonical(latest?.canonical_graph || null);
                setSavedCanonicalVersion(latest?.version ?? null);
                setSavedCanonicalUpdatedAt(latest?.created_at || null);
              }
            } catch {}
            setSaving(false);
            return;
          }
          
          if (!resp.ok) throw new Error(result?.detail || 'Save failed');
          // Close first to avoid any visual flicker while parent updates
          closeEntityModal();
          // Now update parent state
          setSavedCanonical(result?.canonical_graph || null);
          setSavedCanonicalVersion(result?.version ?? null);
          setSavedCanonicalUpdatedAt(result?.created_at || new Date().toISOString());
        }
        
      } catch (e: any) {
        notify(e.message || 'Failed to save entity', 'error');
        setSaving(false);
      }
    };

    return (
      <AppDialog
        open={entityModalOpen}
        onClose={closeEntityModal}
        size="lg"
        title={isUnassignedEntity ? 'Manage unassigned fields' : 'Edit entity'}
        headerExtras={isUnassignedEntity ? (
          <Chip
            icon={<InfoOutlined />}
            label="Special entity"
            size="small"
            color="warning"
            variant="outlined"
          />
        ) : undefined}
        busy={saving}
        dividers
        primaryAction={{
          label: saving
            ? 'Saving…'
            : (isUnassignedEntity && Object.keys(mappingReassignments).length > 0 ? 'Move mappings & save' : 'Save'),
          onClick: handleSave,
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: closeEntityModal }}
        actionsLeftExtra={
          isUnassignedEntity && Object.keys(mappingReassignments).length > 0 ? (
            <Chip
              label={`${Object.keys(mappingReassignments).length} mapping(s) ready to move`}
              color="primary"
              size="small"
            />
          ) : undefined
        }
      >
          <Stack spacing={3}>
            {/* Entity Info Section */}
            <Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Entity Name</Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'action.hover' }}>
                    <Typography variant="h6" fontWeight={600} color="text.secondary">
                      {localData.name || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Read-only
                    </Typography>
                  </Paper>
                </Box>
                <TextField
                  label="Description"
                  value={localData.description || ''}
                  onChange={(e) => setLocalData({...localData, description: e.target.value})}
                  multiline
                  rows={2}
                  fullWidth
                />
                
                {/* Tags */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Tags</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Add tag"
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const tag = newTagInput.trim();
                          if (tag && !(localData.tags || []).includes(tag)) {
                            setLocalData({...localData, tags: [...(localData.tags || []), tag]});
                            setNewTagInput('');
                          }
                        }
                      }}
                      sx={{ width: 200 }}
                    />
                    <Button 
                      size="small" 
                      onClick={() => {
                        const tag = newTagInput.trim();
                        if (tag && !(localData.tags || []).includes(tag)) {
                          setLocalData({...localData, tags: [...(localData.tags || []), tag]});
                          setNewTagInput('');
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                  {(localData.tags || []).length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(localData.tags || []).map((t: string, i: number) => (
                        <Chip 
                          key={i} 
                          label={t} 
                          size="small" 
                          onDelete={() => setLocalData({...localData, tags: localData.tags.filter((tag: string) => tag !== t)})} 
                          sx={{ mb: 0.5 }} 
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                
                {/* Display Fields */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Display Fields</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Select fields to show users (order matters - first field is primary)
                  </Typography>
                  <Autocomplete
                    multiple
                    size="small"
                    options={(localData.fields || []).map((f: any) => f.name).filter(Boolean)}
                    value={localData.display_fields || []}
                    onChange={(_, newValue) => {
                      setLocalData({...localData, display_fields: newValue});
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select display fields"
                        helperText="Choose 1-3 fields that are most useful for users (e.g., name, email)"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          size="small"
                          color={index === 0 ? 'primary' : 'default'}
                        />
                      ))
                    }
                  />
                </Box>
                
                {/* Business Rules */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Business Rules ({(localData.business_rules || []).length})</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Add rules to guide query generation (e.g., "Always exclude deleted_at IS NULL")
                  </Typography>
                  
                  {(localData.business_rules || []).length > 0 && (
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      {(localData.business_rules || []).map((rule: string, i: number) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {i + 1}. {rule}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updated = (localData.business_rules || []).filter((_: string, idx: number) => idx !== i);
                                setLocalData({...localData, business_rules: updated});
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                  
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Type a business rule and press Enter (e.g., 'Always filter deleted_at IS NULL')" 
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = (e.target as HTMLInputElement).value.trim();
                        if (input) {
                          setLocalData({
                            ...localData,
                            business_rules: [...(localData.business_rules || []), input]
                          });
                          (e.target as HTMLInputElement).value = '';
                        }
                      }
                    }}
                    helperText="Examples: 'Exclude soft-deleted records' • 'Use company_id for joins' • 'Sum amount for revenue'"
                  />
                </Box>
              </Stack>
            </Box>

            {/* Fields Section */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  {isUnassignedEntity 
                    ? `Fields (${(localData.fields || []).reduce((total: number, f: any) => total + ((f.mappings || []).length), 0)})`
                    : `Fields (${(localData.fields || []).length})`
                  }
                </Typography>
                {!isUnassignedEntity && (
                  <Button 
                    size="small" 
                    startIcon={<AddIcon />} 
                    onClick={() => {
                      const newField = {
                        name: 'new_field',
                        description: '',
                        semantic_type: '',
                        pii: 'none',
                        primary_key: false,
                        is_join_key: false,
                        nullable: true,
                        mappings: [{ connector_id: '', source_entity: '', source_field: '', confidence: 1.0 }]
                      };
                      setLocalData({...localData, fields: [...(localData.fields || []), newField]});
                    }}
                  >
                    Add Field
                  </Button>
                )}
              </Stack>
              {isUnassignedEntity && (
                <Alert severity="info" sx={{ mb: 2 }} icon={<InfoOutlined />}>
                  <Typography variant="body2">
                    <strong>Batch assign fields by source:</strong> Fields are grouped by their source table. Select fields from each source, choose a target entity for that source, then click Save to move them all at once.
                  </Typography>
                </Alert>
              )}
              
              {isUnassignedEntity ? (
                // NEW: Grouped view by source entity
                <Stack spacing={2} sx={{ width: '100%', minWidth: 0 }}>
                  {Object.entries(mappingsBySource).sort(([a], [b]) => a.localeCompare(b)).map(([sourceEntity, items]) => {
                    const selectedKeys = selectedFieldsBySource[sourceEntity] || new Set();
                    const allKeysInGroup = items.map(item => `${item.fieldIdx}-${item.mappingIdx}`);
                    const allSelected = allKeysInGroup.length > 0 && allKeysInGroup.every(k => selectedKeys.has(k));
                    const someSelected = allKeysInGroup.some(k => selectedKeys.has(k));
                    const targetEntity = targetEntityBySource[sourceEntity];
                    
                    return (
                      <Paper key={sourceEntity} variant="outlined" sx={{ p: 2, width: '100%', overflow: 'hidden' }}>
                        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                          {/* Source header with select all and target entity selector */}
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected && !allSelected}
                              onChange={(e) => {
                                const newSet = new Set(selectedKeys);
                                if (e.target.checked) {
                                  allKeysInGroup.forEach(k => newSet.add(k));
                                } else {
                                  allKeysInGroup.forEach(k => newSet.delete(k));
                                }
                                setSelectedFieldsBySource(prev => ({ ...prev, [sourceEntity]: newSet }));
                              }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {sourceEntity}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {items.length} field{items.length !== 1 ? 's' : ''} {selectedKeys.size > 0 && `• ${selectedKeys.size} selected`}
                              </Typography>
                            </Box>
                            <Autocomplete
                              size="small"
                              options={targetEntities}
                              value={targetEntity || null}
                              onChange={(_, newValue) => {
                                setTargetEntityBySource(prev => ({
                                  ...prev,
                                  [sourceEntity]: newValue || ''
                                }));
                              }}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  placeholder="Select target entity"
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: targetEntity ? (
                                      <ArrowForwardRounded fontSize="small" color="primary" sx={{ mr: 0.5 }} />
                                    ) : null
                                  }}
                                />
                              )}
                              sx={{ width: 280 }}
                            />
                          </Stack>
                          
                          {/* Fields list - Grid layout (4 columns) */}
                          <Box sx={{ pl: 5, pr: 2 }}>
                            <Box sx={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(4, 1fr)', 
                              gap: 1,
                              width: '100%',
                              maxWidth: '100%',
                              overflow: 'hidden'
                            }}>
                              {items.map((item) => {
                                const key = `${item.fieldIdx}-${item.mappingIdx}`;
                                const isSelected = selectedKeys.has(key);
                                const sourceField = item.mapping.source_field || '';
                                
                                return (
                                  <Box
                                    key={key}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      py: 0.5,
                                      px: 1,
                                      borderRadius: 1,
                                      backgroundColor: isSelected ? 'action.selected' : 'transparent',
                                      minWidth: 0,
                                      '&:hover': {
                                        backgroundColor: isSelected ? 'action.selected' : 'action.hover'
                                      }
                                    }}
                                  >
                                    <Checkbox 
                                      size="small"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        const newSet = new Set(selectedKeys);
                                        if (e.target.checked) {
                                          newSet.add(key);
                                        } else {
                                          newSet.delete(key);
                                        }
                                        setSelectedFieldsBySource(prev => ({ ...prev, [sourceEntity]: newSet }));
                                      }}
                                    />
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        flex: 1, 
                                        fontWeight: 500,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0
                                      }}
                                      title={sourceField}
                                    >
                                      {sourceField}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                  
                  {Object.keys(mappingsBySource).length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No unassigned fields.
                    </Typography>
                  )}
                </Stack>
              ) : (
                // Normal table view for other entities
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 160 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 240 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Semantic Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 100 }}>PII</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Confidence</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 100 }}>Source</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 60 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {
                      // Normal field view for other entities
                      (localData.fields || []).map((field: any, fieldIdx: number) => {
                        const mappings = field.mappings || [];
                        const conf = mappings.length > 0 
                          ? mappings.reduce((acc: number, m: any) => acc + (m.confidence || 0), 0) / mappings.length 
                          : null;
                        const mappingsCount = mappings.length;
                        
                        return (
                          <TableRow key={fieldIdx}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={field.name || ''}
                                onChange={(e) => {
                                  const updated = [...localData.fields];
                                  updated[fieldIdx] = {...updated[fieldIdx], name: e.target.value};
                                  setLocalData({...localData, fields: updated});
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={field.description || ''}
                                onChange={(e) => {
                                  const updated = [...localData.fields];
                                  updated[fieldIdx] = {...updated[fieldIdx], description: e.target.value};
                                  setLocalData({...localData, fields: updated});
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={field.semantic_type || ''}
                                onChange={(e) => {
                                  const updated = [...localData.fields];
                                  updated[fieldIdx] = {...updated[fieldIdx], semantic_type: e.target.value};
                                  setLocalData({...localData, fields: updated});
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                size="small"
                                value={field.pii || 'none'}
                                onChange={(e) => {
                                  const updated = [...localData.fields];
                                  updated[fieldIdx] = {...updated[fieldIdx], pii: e.target.value};
                                  setLocalData({...localData, fields: updated});
                                }}
                                fullWidth
                              >
                                <MenuItem value="none">none</MenuItem>
                                <MenuItem value="low">low</MenuItem>
                                <MenuItem value="medium">medium</MenuItem>
                                <MenuItem value="high">high</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                {formatConfidenceAsPercent(conf !== null ? conf : 1.0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={`${mappingsCount} source mapping(s)`}>
                                <Chip size="small" label={mappingsCount} variant="outlined" />
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={() => {
                                  const updated = localData.fields.filter((_: any, i: number) => i !== fieldIdx);
                                  setLocalData({...localData, fields: updated});
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    }
                  </TableBody>
                </Table>
              </Box>
              )}
            </Box>

            {/* Relationships Section (Read-only) */}
            {entityRelationships.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Relationships ({entityRelationships.length})
                </Typography>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Stack spacing={1}>
                    {entityRelationships.map((rel: any, idx: number) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={rel.from_entity === localData.name ? 'From' : 'To'}
                            color={rel.from_entity === localData.name ? 'primary' : 'secondary'}
                          />
                          <Typography variant="body2">
                            <strong>{rel.from_entity}</strong> → <strong>{rel.to_entity}</strong>
                          </Typography>
                          <Chip size="small" label={rel.type} variant="outlined" />
                          {typeof rel.confidence === 'number' && (
                            <Chip size="small" label={formatConfidenceAsPercent(rel.confidence)} variant="outlined" />
                          )}
                        </Stack>
                        {Array.isArray(rel.join_on) && rel.join_on.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {rel.join_on.map((j: any) => `${j.from_field} = ${j.to_field}`).join(', ')}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}

          </Stack>
      </AppDialog>
    );
  }

  function EditableRelationshipsTable({ rels }: { rels: any[] }) {
    const entityNames = (draftCanonical?.entities || []).map((e: any) => e.name as string);
    const fieldsByEntity: Record<string, string[]> = Object.fromEntries((draftCanonical?.entities || []).map((e: any) => [e.name, (e.fields || []).map((f: any) => f.name)]));
    const onRelChange = (k: number, patch: any) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        next.relationships[k] = { ...(next.relationships[k] || {}), ...patch };
        return next;
      });
    };
    const addRel = () => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || { relationships: [] }));
        (next.relationships = next.relationships || []).push({ from_entity: '', to_entity: '', type: 'unknown', join_on: [], confidence: 0 });
        return next;
      });
    };
    const removeRel = (k: number) => {
      setDraftCanonical((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev || {}));
        next.relationships.splice(k, 1);
        return next;
      });
    };
    return (
      <Box>
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>From Entity</TableCell>
              <TableCell>From Field</TableCell>
              <TableCell>To Entity</TableCell>
              <TableCell>To Field</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Act</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rels || []).map((r: any, k: number) => (
              <TableRow key={k}>
                <TableCell>
                  <Autocomplete size="small" sx={{ minWidth: 180 }} options={entityNames}
                    value={r.from_entity || ''}
                    onChange={(_, val) => onRelChange(k, { from_entity: val || '', from_field: '' })}
                    renderInput={(params) => <TextField {...params} placeholder="entity" />} />
                </TableCell>
                <TableCell>
                  <Autocomplete size="small" sx={{ minWidth: 240 }} multiple options={((fieldsByEntity[r.from_entity] || []) as string[])}
                    value={Array.isArray(r.join_on) ? r.join_on.map((p: any) => p.from_field) : []}
                    onChange={(_, vals) => {
                      const pairs = (vals as string[]).map((ff: string, idx: number) => ({ from_field: ff, to_field: (r.join_on?.[idx]?.to_field) || '' }));
                      onRelChange(k, { join_on: pairs });
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="from fields" />} />
                </TableCell>
                <TableCell>
                  <Autocomplete size="small" sx={{ minWidth: 180 }} options={entityNames}
                    value={r.to_entity || ''}
                    onChange={(_, val) => onRelChange(k, { to_entity: val || '', join_on: [] })}
                    renderInput={(params) => <TextField {...params} placeholder="entity" />} />
                </TableCell>
                <TableCell>
                  <Autocomplete size="small" sx={{ minWidth: 240 }} multiple options={((fieldsByEntity[r.to_entity] || []) as string[])}
                    value={Array.isArray(r.join_on) ? r.join_on.map((p: any) => p.to_field) : []}
                    onChange={(_, vals) => {
                      const pairs = (vals as string[]).map((tf: string, idx: number) => ({ from_field: (r.join_on?.[idx]?.from_field) || '', to_field: tf }));
                      onRelChange(k, { join_on: pairs });
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="to fields" />} />
                </TableCell>
                  <TableCell><TextField size="small" type="text" inputProps={{ readOnly: true }} sx={{ width: 120 }} value={(() => { const rc = getFirstConfidenceValue(r.confidence); return formatConfidenceAsPercent(rc); })()} /></TableCell>
                <TableCell><IconButton size="small" color="error" onClick={() => removeRel(k)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={6}>
                <Button size="small" startIcon={<AddIcon />} onClick={addRel}>Add Relationship</Button>
              </TableCell>
            </TableRow>
          </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  }

  // ────────── Derived data for the workbench ─────────────
  // Built once per render from savedCanonical + filters/sort. We don't memoize
  // because savedCanonical is small and the derivations are O(entities + rels).
  const allEntities: any[] = Array.isArray((savedCanonical as any)?.entities) ? (savedCanonical as any).entities : [];
  const allRels: any[] = Array.isArray((savedCanonical as any)?.relationships) ? (savedCanonical as any).relationships : [];

  // entityName -> count of relationships it appears in (either side)
  const relationshipCounts: Record<string, number> = {};
  allRels.forEach((r: any) => {
    const a = String(r?.from_entity || '');
    const b = String(r?.to_entity || '');
    if (a) relationshipCounts[a] = (relationshipCounts[a] || 0) + 1;
    if (b && b !== a) relationshipCounts[b] = (relationshipCounts[b] || 0) + 1;
  });

  // entityName -> list of source-connector groups for the card footer.
  // Each group is one *connector* (not one type) — so two distinct Postgres
  // connectors render as two icons with separate hover tooltips.
  // If a mapping references a connector_id we can't resolve, we drop that
  // mapping silently rather than leak a UUID into the UI.
  const connectorMetaById = React.useMemo(() => {
    const m: Record<string, { type: string; name: string }> = {};
    (connectors || []).forEach((c: any) => {
      if (c?.connector_id && c?.type) {
        m[c.connector_id] = {
          type: c.type,
          name: c.name || prettyConnectorType(c.type),
        };
      }
    });
    return m;
  }, [connectors]);
  type SourceGroup = { connectorId: string; type: string; name: string; tables: string[] };
  const sourceGroupsByEntity: Record<string, SourceGroup[]> = {};
  // Set of distinct connector types across the model — drives the Source filter dropdown
  const allSourceTypes = new Set<string>();
  allEntities.forEach((e: any) => {
    const byConn: Record<string, SourceGroup> = {};
    const fields = Array.isArray(e?.fields) ? e.fields : [];
    fields.forEach((f: any) => {
      const ms = Array.isArray(f?.mappings) ? f.mappings : [];
      ms.forEach((m: any) => {
        const cid = m?.connector_id ? String(m.connector_id) : '';
        const meta = cid ? connectorMetaById[cid] : undefined;
        if (!meta) return;
        allSourceTypes.add(meta.type);
        if (!byConn[cid]) {
          byConn[cid] = { connectorId: cid, type: meta.type, name: meta.name, tables: [] };
        }
        const tbl = String(m?.source_entity || '').trim();
        if (tbl && !byConn[cid].tables.includes(tbl)) byConn[cid].tables.push(tbl);
      });
    });
    const groups = Object.values(byConn);
    if (groups.length > 0) sourceGroupsByEntity[String(e?.name || '')] = groups;
  });

  // Aggregate field-mapping health for the toolbar pill
  // ("mapped" = at least one connector_id on at least one mapping)
  const fieldHealth = (() => {
    let total = 0;
    let mapped = 0;
    allEntities.forEach((e: any) => {
      const fields = Array.isArray(e?.fields) ? e.fields : [];
      fields.forEach((f: any) => {
        total += 1;
        const ms = Array.isArray(f?.mappings) ? f.mappings : [];
        if (ms.some((m: any) => !!m?.connector_id || !!m?.source_field)) mapped += 1;
      });
    });
    return { mapped, total };
  })();

  // Search + filter pipeline. We want the search to be "deep": matching an
  // entity by name/desc/tag is obvious, but also surface entities whose
  // fields match — that's how analysts actually look for data.
  const queryNorm = entitySearch.trim().toLowerCase();
  const matchesQuery = (e: any): boolean => {
    if (!queryNorm) return true;
    const name = String(e?.name || '').toLowerCase();
    const desc = String(e?.description || '').toLowerCase();
    if (name.includes(queryNorm) || desc.includes(queryNorm)) return true;
    const tags = Array.isArray(e?.tags) ? e.tags : [];
    if (tags.some((t: string) => String(t).toLowerCase().includes(queryNorm))) return true;
    const fields = Array.isArray(e?.fields) ? e.fields : [];
    return fields.some((f: any) => String(f?.name || '').toLowerCase().includes(queryNorm));
  };
  const matchesSourceFilter = (e: any): boolean => {
    if (sourceFilter === 'all') return true;
    const groups = sourceGroupsByEntity[String(e?.name || '')];
    return !!groups && groups.some((g) => g.type === sourceFilter);
  };

  const filteredEntities = allEntities.filter((e) => matchesQuery(e) && matchesSourceFilter(e));

  // Sort
  const sortedEntities = [...filteredEntities].sort((a, b) => {
    if (sortKey === 'name') {
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    }
    if (sortKey === 'fields_asc') {
      return (a?.fields?.length || 0) - (b?.fields?.length || 0);
    }
    if (sortKey === 'updated') {
      // No per-entity timestamp from backend; fall back to relationship density
      // as a rough proxy for "active" entities until backend exposes a real one.
      const aR = relationshipCounts[String(a?.name || '')] || 0;
      const bR = relationshipCounts[String(b?.name || '')] || 0;
      return bR - aR;
    }
    return (b?.fields?.length || 0) - (a?.fields?.length || 0); // fields_desc default
  });

  // Filter relationships to those connecting at least one visible entity.
  const visibleEntityNames = new Set(filteredEntities.map((e: any) => e?.name));
  const visibleRels = (queryNorm || sourceFilter !== 'all')
    ? allRels.filter((r: any) => visibleEntityNames.has(r?.from_entity) || visibleEntityNames.has(r?.to_entity))
    : allRels;

  const totalFieldCount = allEntities.reduce((acc: number, e: any) => acc + (Array.isArray(e?.fields) ? e.fields.length : 0), 0);
  const allConfidences = allRels.map((r: any) => (typeof r?.confidence === 'number' ? r.confidence : null)).filter((v: number | null) => v !== null) as number[];
  const avgConf = allConfidences.length ? (allConfidences.reduce((a, b) => a + b, 0) / allConfidences.length) : null;

  const sortLabel: Record<EntitySortKey, string> = {
    name: 'Name (A→Z)',
    fields_desc: 'Most fields',
    fields_asc: 'Fewest fields',
    updated: 'Most connected',
  };

  const hasActiveFilters = !!queryNorm || sourceFilter !== 'all';

  return (
    <Box sx={{ width: '100%' }}>
      {loading ? <CircularProgress /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          {/* ── Page header ────────────────────────────────────────────
              No in-page H5 title — the breadcrumb above already says "Data
              Model", so repeating it here was redundant and made this page
              taller than every other page (Connectors, Knowledge Base) that
              relies on the breadcrumb alone. The version + "Updated" chips
              still live here on the left as a thin metadata row above the
              subtitle, and the primary "Rebuild" CTA + kebab stay on the
              right. */}
          <Box sx={{
            mb: 2.5,
            pb: 1.5,
            borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, sm: 1.5 },
          }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {(savedCanonicalVersion || savedCanonicalUpdatedAt) ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 0.75 }}
                >
                  {savedCanonicalVersion ? (
                    <Tooltip title="Canonical schema version. Increments on every save — rebuilds and manual edits both produce a new version.">
                      <Chip
                        size="small"
                        label={`v${savedCanonicalVersion}`}
                        variant="outlined"
                        sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                      />
                    </Tooltip>
                  ) : null}
                  {/* "Updated" not "Rebuilt": the backend writes a new canonical
                      row on every save (rebuild OR manual edit — see
                      backend/routers/relationships.py PATCH endpoints), so this
                      timestamp reflects the last save of any kind. Labelling it
                      "Rebuilt" was misleading users who'd just edited a field. */}
                  {savedCanonicalUpdatedAt ? (
                    <Tooltip title={formatAbsolute(savedCanonicalUpdatedAt)}>
                      <Chip
                        size="small"
                        icon={<HistoryRounded sx={{ fontSize: 14 }} />}
                        label={`Updated ${formatRelativeTime(savedCanonicalUpdatedAt)}`}
                        variant="outlined"
                        sx={{ fontSize: '0.6875rem', height: 22 }}
                      />
                    </Tooltip>
                  ) : null}
                </Stack>
              ) : null}
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45 }}>
                AI-discovered entities and the relationships that join them across your sources.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshRounded sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/data-sync')}
                sx={{ fontWeight: 600, whiteSpace: 'nowrap', flex: { xs: 1, sm: 'unset' } }}
              >
                {isMobileLayout ? 'Rebuild' : 'Rebuild data model'}
              </Button>
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  onClick={(ev) => setMoreMenuAnchor(ev.currentTarget)}
                  aria-label="More actions"
                  sx={(t) => ({
                    border: `1px solid ${t.tokens.border.default}`,
                    borderRadius: `${t.ds.radius.sm}px`,
                    width: 32, height: 32,
                  })}
                >
                  <MoreVertRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Overflow menu — secondary actions live here, not the header bar */}
          <Menu
            anchorEl={moreMenuAnchor}
            open={!!moreMenuAnchor}
            onClose={() => setMoreMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => { setMoreMenuAnchor(null); exportEntities(); }}
              disabled={exportLoading}
            >
              <ListItemIcon>{exportLoading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={exportLoading ? 'Exporting…' : 'Export entities (CSV)'} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMoreMenuAnchor(null);
                try {
                  navigator.clipboard.writeText(JSON.stringify(savedCanonical || {}, null, 2));
                  notify('JSON copied to clipboard', 'success');
                } catch {
                  notify('Failed to copy to clipboard', 'error');
                }
              }}
              disabled={!savedCanonical}
            >
              <ListItemIcon><ContentCopyRounded fontSize="small" /></ListItemIcon>
              <ListItemText primary="Copy canonical JSON" />
            </MenuItem>
          </Menu>

          {/* Enrichment status banner — moved up so it sits above the workbench */}
          {enrichmentStatus && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              onClose={() => {
                window.localStorage.removeItem('klaris_enrichment_status');
                setEnrichmentStatus(null);
                if (pollingInterval) clearInterval(pollingInterval);
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                <CircularProgress size={16} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Enrichment in progress... {(() => {
                      const seconds = Math.floor(enrichmentElapsed / 1000);
                      const minutes = Math.floor(seconds / 60);
                      const hours = Math.floor(minutes / 60);
                      if (hours > 0) return `(${hours}h ${minutes % 60}m)`;
                      if (minutes > 0) return `(${minutes}m ${seconds % 60}s)`;
                      return `(${seconds}s)`;
                    })()}
                  </Typography>
                  <Typography variant="caption">Current results are shown below. They will refresh automatically when enrichment completes.</Typography>
                </Box>
              </Stack>
            </Alert>
          )}

          {savedCanonicalLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (savedCanonical && allEntities.length > 0) ? (
            <Box sx={{ opacity: enrichmentStatus ? 0.7 : 1, transition: 'opacity 0.3s' }}>
              {/* Unassigned-fields alert (kept up top — it's an action, not chrome) */}
              {(() => {
                const unassignedEnt = allEntities.find((e: any) =>
                  Array.isArray(e?.tags) && e.tags.some((t: string) => t.toLowerCase() === 'unassigned')
                );
                const fieldCount = unassignedEnt ? (unassignedEnt.fields || []).reduce(
                  (acc: number, f: any) => acc + (Array.isArray(f?.mappings) ? f.mappings.length : 0), 0
                ) : 0;
                if (!unassignedEnt || fieldCount === 0) return null;
                return (
                  <Alert
                    severity="warning"
                    icon={<WarningRounded fontSize="inherit" />}
                    sx={{ mb: 2 }}
                    action={
                      <Button
                        color="warning"
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/data-model/entity/${encodeURIComponent(unassignedEnt.name)}`)}
                      >
                        Assign now
                      </Button>
                    }
                  >
                    <strong>{fieldCount} field{fieldCount !== 1 ? 's' : ''} need assignment</strong> — new columns were detected that couldn't be automatically placed. Click <strong>Assign now</strong> to move them to the right entities.
                  </Alert>
                );
              })()}

              {/* ── Stat tiles — flat row (no inconsistent sparkline) ───── */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2.5 }}>
                {([
                  { label: 'Entities', value: allEntities.length, icon: <LayersRounded sx={{ fontSize: 16 }} /> },
                  { label: 'Fields', value: totalFieldCount, icon: <LabelImportantRounded sx={{ fontSize: 16 }} /> },
                  { label: 'Relationships', value: allRels.length, icon: <LinkRounded sx={{ fontSize: 16 }} /> },
                  { label: 'Avg. confidence', value: formatConfidenceAsPercent(avgConf), icon: <AutoAwesomeRounded sx={{ fontSize: 16 }} /> },
                ] as Array<{ label: string; value: React.ReactNode; icon: React.ReactNode }>).map((stat, i) => (
                  <Paper key={i} variant="outlined" sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px` })}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={(t) => ({ color: t.tokens.text.tertiary })}>
                      {stat.icon}
                      <Typography variant="caption" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                        {stat.label}
                      </Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, fontSize: '1.5rem', lineHeight: 1.2 }}>{stat.value}</Typography>
                  </Paper>
                ))}
              </Box>

              {/* ── Toolbar: search + filters + sort + health pill ─────── */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={(t) => ({
                  mb: 1.5,
                  p: 1,
                  borderRadius: `${t.ds.radius.md}px`,
                  border: `1px solid ${t.tokens.border.subtle}`,
                  bgcolor: t.tokens.surface.sunken,
                })}
              >
                <TextField
                  size="small"
                  placeholder="Search entities, fields, descriptions…"
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                  fullWidth
                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                  }}
                />

                {/* Source filter — only shown when there's >1 connector type to choose from */}
                {allSourceTypes.size > 1 && (
                  <Select
                    size="small"
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(String(e.target.value))}
                    displayEmpty
                    sx={{ minWidth: 180, bgcolor: 'background.paper' }}
                    renderValue={(v) => (
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        {v === 'all' ? (
                          <StorageRounded sx={{ fontSize: 14 }} />
                        ) : (
                          <Box sx={{ width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', '& > svg': { width: 14, height: 14 } }}>
                            <ConnectorIcon type={String(v)} size={14} />
                          </Box>
                        )}
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem' }}>
                          {v === 'all' ? 'All sources' : prettyConnectorType(String(v))}
                        </Typography>
                      </Stack>
                    )}
                  >
                    <MenuItem value="all">All sources</MenuItem>
                    {Array.from(allSourceTypes).sort().map((tp) => (
                      <MenuItem key={tp} value={tp} sx={{ fontSize: '0.8125rem' }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <Box sx={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', '& > svg': { width: 14, height: 14 } }}>
                            <ConnectorIcon type={tp} size={14} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText primary={prettyConnectorType(tp)} />
                      </MenuItem>
                    ))}
                  </Select>
                )}

                {/* Sort */}
                <Tooltip title="Sort entities">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SortRounded sx={{ fontSize: 16 }} />}
                    endIcon={<ExpandMoreRounded sx={{ fontSize: 16 }} />}
                    onClick={(ev) => setSortMenuAnchor(ev.currentTarget)}
                    sx={{ bgcolor: 'background.paper', whiteSpace: 'nowrap', justifyContent: 'space-between' }}
                  >
                    {sortLabel[sortKey]}
                  </Button>
                </Tooltip>
                <Menu
                  anchorEl={sortMenuAnchor}
                  open={!!sortMenuAnchor}
                  onClose={() => setSortMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {(Object.keys(sortLabel) as EntitySortKey[]).map((k) => (
                    <MenuItem key={k} selected={sortKey === k} onClick={() => { setSortKey(k); setSortMenuAnchor(null); }}>
                      <ListItemIcon>{sortKey === k ? <CheckRounded fontSize="small" /> : <Box sx={{ width: 20 }} />}</ListItemIcon>
                      <ListItemText primary={sortLabel[k]} />
                    </MenuItem>
                  ))}
                </Menu>

                {/* Persistent fields-health pill: at-a-glance "are my fields mapped?" */}
                {fieldHealth.total > 0 && (() => {
                  const ratio = fieldHealth.mapped / fieldHealth.total;
                  const tone = ratio === 1 ? 'success' : ratio >= 0.9 ? 'info' : 'warning';
                  return (
                    <Tooltip title={`${fieldHealth.mapped} of ${fieldHealth.total} fields are mapped to a source column`}>
                      <Chip
                        size="small"
                        color={tone as any}
                        variant={tone === 'success' ? 'filled' : 'outlined'}
                        label={`${fieldHealth.mapped}/${fieldHealth.total} mapped`}
                        sx={{ fontSize: '0.6875rem', height: 26, fontWeight: 600, alignSelf: 'center' }}
                      />
                    </Tooltip>
                  );
                })()}
              </Stack>

              {/* Result-count caption — only shown when a filter is active.
                  At rest the page header's stats tile already shows the
                  totals, so re-stating "4 entities · 30 fields" here would
                  be redundant. When filtering, this row tells the user how
                  much got narrowed and offers a one-click clear. */}
              {hasActiveFilters && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {`Showing ${filteredEntities.length} of ${allEntities.length} ${allEntities.length === 1 ? 'entity' : 'entities'}${queryNorm ? ` for "${entitySearch.trim()}"` : ''}`}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => { setEntitySearch(''); setSourceFilter('all'); }}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 22, py: 0, px: 0.75 }}
                  >
                    Clear filters
                  </Button>
                </Stack>
              )}

              {/* ── Entity grid ─────────────────────────────────────────── */}
              <Box sx={{ mb: 4 }}>
                {sortedEntities.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No entities match your filters.
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => { setEntitySearch(''); setSourceFilter('all'); }}
                    >
                      Clear filters
                    </Button>
                  </Paper>
                ) : (
                  <EntityCards
                    entities={sortedEntities}
                    sourceGroupsByEntity={sourceGroupsByEntity}
                    onEntityClick={(_idx: number, entity: any) => navigate(`/data-model/entity/${encodeURIComponent(entity.name)}`)}
                  />
                )}
              </Box>

              {/* ── Relationships block — keeps its own card so it reads as a
                  distinct, secondary section from the entities grid above. */}
              <Card variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px` })}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: { xs: 1.25, sm: 0 },
                    mb: 2,
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LinkRounded sx={(t) => ({ color: t.tokens.text.secondary, fontSize: 18 })} />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.0625rem' } }}>
                        Relationships
                      </Typography>
                      <Chip
                        size="small"
                        label={visibleRels.length}
                        variant="outlined"
                        sx={{ fontSize: '0.6875rem', height: 20, fontWeight: 600 }}
                      />
                    </Stack>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={() => openAddRelationshipModal()}
                      sx={{ alignSelf: { xs: 'stretch', sm: 'auto' }, textTransform: 'none', fontWeight: 600 }}
                    >
                      Add relationship
                    </Button>
                  </Box>
                  <RelationshipsListV2
                    rels={visibleRels}
                    onRelationshipClick={openRelationshipModal}
                    isMobile={isMobileLayout}
                    collapsedGroups={collapsedRelGroups}
                    onToggleGroup={(k: string) =>
                      setCollapsedRelGroups((prev: Record<string, boolean>) => ({ ...prev, [k]: !prev[k] }))
                    }
                  />
                </CardContent>
              </Card>

              {canonicalMessage && (
                <Alert severity={canonicalMessage.includes('saved') || canonicalMessage.includes('completed') ? 'success' : 'error'} sx={{ mt: 2 }}>{canonicalMessage}</Alert>
              )}
            </Box>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 1.5, border: '1px dashed', borderColor: 'divider' }}>
              <AutoAwesomeRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                No data model yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Run the AI pipeline to discover entities, map fields, and generate relationships across your connected data sources.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AccountTreeRounded />}
                onClick={() => navigate('/data-sync')}
                sx={{ fontWeight: 600 }}
              >
                Build Data Model
              </Button>
            </Paper>
          )}

          {/* Relationship Edit Modal */}
          <RelationshipEditModal />

          {/* Removed schema preview modal for simplified selection UI */}
        </>
      )}
    </Box>
  );
}
